/**
 * A custom React hook that manages the splitting of comments into containers.
 * It ensures that each container has at least 2 comments and fits within the page height.
 *
 * @returns {Object} An object containing the `splitCommentCards` function.
 */
export function useHandleStartKeepDoingContainers() {
  const MIN_COMMENTS_PER_CONTAINER = 2; // 🚀 At least 2 comments per container
  const MM_TO_PX = 3.78; // Conversion from millimeters to pixels

  /**
   * Gets all comment elements within a container.
   */
  const getCommentCards = (container: HTMLElement): HTMLElement[] => {
    return Array.from(container.querySelectorAll('.print-comment-card'));
  };

  /**
   * Gets the initial height of the container (considering the header).
   */
  const getInitialHeight = (container: HTMLElement): number => {
    const header = container
      .querySelector('.print-card-header')
      ?.getBoundingClientRect();
    return header ? header.height : 20;
  };

  /**
   * Creates a new comment container and adds it to the DOM after the original container.
   */
  const createNewContainer = (container: HTMLElement): HTMLElement => {
    const newContainer = container.cloneNode(true) as HTMLElement;
    newContainer.querySelector('.print-card-body')!.innerHTML = ''; // Clear existing comments
    // newContainer.classList.add('new-page'); // Force page break
    container.after(newContainer);
    return newContainer;
  };

  /**
   * Adds a list of comments to the specified container.
   */
  const appendCommentsToContainer = (
    comments: HTMLElement[],
    container: HTMLElement
  ) => {
    const body = container.querySelector('.print-card-body');
    comments.forEach(comment => body?.appendChild(comment));
  };

  /**
   * Removes a container if it is empty.
   */
  const removeEmptyContainer = (container: HTMLElement) => {
    if (container.querySelector('.print-card-body')?.children.length === 0) {
      container.remove();
    }
  };

  /**
   * Manages the splitting of comments into containers, ensuring proper page breaks.
   */
  const splitCommentCards = (container: HTMLElement, pageHeightMM: number) => {
    const pageHeight = pageHeightMM * MM_TO_PX; // Convert page height to pixels
    const commentCards = getCommentCards(container);
    let currentContainer = container;
    let currentHeight = getInitialHeight(container);
    let buffer: HTMLElement[] = [];
    let bufferHeight = 0;

    commentCards.forEach(comment => {
      const commentHeight = comment.getBoundingClientRect().height;

      buffer.push(comment);
      bufferHeight += commentHeight;

      // Rule 1: Always ensure at least 2 comments per container
      if (buffer.length < MIN_COMMENTS_PER_CONTAINER) return;

      // If the buffer fits within the page, add to the current container and continue
      if (currentHeight + bufferHeight <= pageHeight) {
        appendCommentsToContainer(buffer, currentContainer);
        currentHeight += bufferHeight;
      } else {
        // If it doesn't fit, create a new container and add the comments there
        currentContainer = createNewContainer(container);
        appendCommentsToContainer(buffer, currentContainer);
        currentHeight = getInitialHeight(currentContainer) + bufferHeight;
      }

      buffer = [];
      bufferHeight = 0;
    });

    // Ensure empty containers are removed
    removeEmptyContainer(container);
  };

  return { splitCommentCards };
}
